import React, { PureComponent } from 'react';
import { layer, source, feature, MapContext } from 'react-openlayers';
import { convertProjection4329, generateColor } from '../../../utils';
import { LocalAtRecord } from '../../../db';
import { RobotsFieldSet } from '@rogoag/airtable/crm';

interface LiveRobotLayerProps {
  allRobots: LocalAtRecord[];
}

interface LiveRobotLayerState {
  robotFeatures: {
    [key: string]: {
      coords: number[];
      robotHeading: number;
    };
  };
  hoveredRobot?: string | null;
}

export default class LiveRobotLayer extends PureComponent<LiveRobotLayerProps, LiveRobotLayerState> {
  static contextType = MapContext;
  declare context: React.ContextType<typeof MapContext>;

  // See MapInteractionsLayer.waitForMap for the full rationale. Any
  // unmount with a pending retry leaks the callback onto the old
  // instance, which then attaches a SECOND pointermove listener to
  // the live map — hover handling then fires twice per move.
  waitForMapTimer: NodeJS.Timeout | undefined;

  constructor(props: LiveRobotLayerProps) {
    super(props);

    this.state = {
      robotFeatures: {},
      hoveredRobot: null,
    };
  }

  async componentDidMount() {
    await this.loadAllRobots();
    this.waitForMap();
  }

  waitForMap = () => {
    if (this.context?.map) {
      this.context.map.on('pointermove', this.handlePointerMove);
    } else {
      this.waitForMapTimer = setTimeout(this.waitForMap, 100); // try again in 100ms
    }
  };

  async componentDidUpdate(prevProps) {
    if (this.props.allRobots !== prevProps.allRobots) {
      await this.loadAllRobots();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.waitForMapTimer);
    this.waitForMapTimer = undefined;
    this.detachPointerMoveListener();
  }

  detachPointerMoveListener() {
    const map = this.context?.map;

    if (map) {
      map.un('pointermove', this.handlePointerMove);
    }
  }

  handlePointerMove = (event: any) => {
    const map = this.context?.map;

    if (!map) {
      return;
    }

    const pixel = map.getEventPixel(event.originalEvent);
    const featureAtPixel = map.forEachFeatureAtPixel(pixel, (feat) => feat);

    if (featureAtPixel) {
      const name = featureAtPixel.get('name');

      if (name && this.state.robotFeatures[name]) {
        this.setState({ hoveredRobot: name });

        return;
      }
    }

    if (this.state.hoveredRobot !== null) {
      this.setState({ hoveredRobot: null });
    }
  };

  /**
   * Loads all of the robot data and parses it into a nice format
   */
  async loadAllRobots() {
    const robotFeatures = {};

    for (const robotRecord of this.props.allRobots) {
      const robot = LocalAtRecord.getRecord<RobotsFieldSet>(robotRecord.table, robotRecord.id);

      if (!robot) {
        continue;
      }

      let coords = [0, 0];
      // these are, stupidly, strings. So we must parse them into floats
      const robotLat = robot.get('Lat');
      const robotLon = robot.get('Lon');
      const robotHeading = robot.get('Heading');

      if (robotLat && robotLon) {
        coords = convertProjection4329([parseFloat(robotLon), parseFloat(robotLat)]);
      }

      const robotName = robot.get('Name');

      if (!robotName) {
        console.error('Robot missing name', robot);
        continue;
      }

      robotFeatures[robotName] = { robotHeading, coords };
    }

    this.setState({ robotFeatures });
  }

  render() {
    console.debug(`LiveRobotLayer: ${Object.keys(this.state.robotFeatures).length} robots`);
    const { robotFeatures, hoveredRobot } = this.state;

    return (
      // for some reason, layer.Vector doesn't have a children prop? So we have to TS-ignore it...
      // @ts-ignore
      <layer.Vector zIndex={2}>
        {/* @ts-ignore */}
        <source.VectorSourceReact>
          {Object.entries(robotFeatures).map(([robot, { coords }]) => (
            <feature.PointReact
              key={robot}
              coordinate={coords}
              properties={{ name: robot }}
              iconOptions={{
                src: './images/location_nocolor.png',
                scale: 0.65,
                color: generateColor(robot),
                rotation: this.state.robotFeatures[robot].robotHeading
                  ? this.state.robotFeatures[robot].robotHeading * -1
                  : 0,
              }}
            />
          ))}

          {/* Hover label */}
          {hoveredRobot && robotFeatures[hoveredRobot] && (
            <feature.PointReact
              key={`hover-label-${hoveredRobot}`}
              coordinate={robotFeatures[hoveredRobot].coords}
              properties={{ name: hoveredRobot }}
              textOptions={{
                text: hoveredRobot,
                offsetY: -20,
                font: '14px Calibri,sans-serif',
              }}
            />
          )}
        </source.VectorSourceReact>
      </layer.Vector>
    );
  }
}
